import { useCallback, useEffect, useRef, useState } from 'react';
import { getPrintConfig, type PrintConfigDto, type PrintDestinationDto } from '@/api/client';
import { renderZpl, isCompleteZpl } from '@/lib/zpl';

/**
 * ============================================================================
 * ⛔⛔ PRINTING, AND THE ONE RULE: IT MUST NEVER SILENTLY DO NOTHING
 * ============================================================================
 *
 * > Pete, 2026-09-09: *"we need to be able to print from Scanned Paperwork …
 * > it could be a 4x6 label to a zebra label printer"*
 *
 * ⭐ SAME TRADE AS `useFileUpload`. Before 2026-09-08 three components each had
 * their own upload loop and the copies drifted — that is what #288 was. Two
 * applications will print (Dock's paperwork and Pipeline's laborclock freight
 * labels), so the loop lives here once.
 *
 * ---- ⛔⛔ EVERY FAILURE GETS ITS OWN SENTENCE ------------------------------
 *
 * ⚠️ A PRINT THAT QUIETLY DOES NOTHING IS THE WORST OUTCOME ON A DOCK. There
 * are six distinct ways this fails and §1AK.1's runbook can only be followed if
 * the screen says WHICH:
 *
 *   · the agent is not installed          → §1AK.1 C1
 *   · the agent will not answer           → C2 (certificate) or C3 (host)
 *   · the configured printer is not there → B2, the name does not match
 *   · no route for this document type     → B3, and that is CORRECT by default
 *   · the config could not be read        → migration 096 or its grant
 *   · the ZPL was refused                 → B4
 *
 * ⭐ AND THE FALLBACK IS ALWAYS THE OS DIALOG, WHICH ALWAYS WORKS. It needs no
 * agent, no certificate and no network.
 *
 * ---- ⚠️ THE SDK IS A GLOBAL, NOT A MODULE ---------------------------------
 *
 * `BrowserPrint-3.1.250.min.js` assigns `window.BrowserPrint` and predates ES
 * modules, which is why nothing here imports it. The method names below were
 * read out of the shipped file, not the vendor's documentation page — reading
 * the bytes is what caught that the SDK calls `127.0.0.1`, never `localhost`.
 * ============================================================================
 */

/** ⭐ Only the parts of the SDK this file uses. Read from the shipped bytes. */
interface ZebraDevice {
    name?: string;
    uid?: string;
    deviceType?: string;
    send(data: string, success?: () => void, error?: (m: string) => void): void;
}
interface ZebraSdk {
    getLocalDevices(
        success: (devices: ZebraDevice[] | Record<string, ZebraDevice[]>) => void,
        error?: (m: string) => void,
        type?: string,
    ): void;
    getDefaultDevice(
        type: string,
        success: (device: ZebraDevice | null) => void,
        error?: (m: string) => void,
    ): void;
}
declare global {
    interface Window { BrowserPrint?: ZebraSdk }
}

export type PrintRoute = 'ZEBRA' | 'DIALOG';

export interface PrintOutcome {
    ok: boolean;
    /** ⭐ Which path actually ran — not which one was configured. */
    via: PrintRoute;
    /** ⛔ A sentence for a human, always set, success or failure. */
    message: string;
}

/**
 * ⭐ `zplSafe`, `renderZpl` and `isCompleteZpl` live in `@/lib/zpl` — no React,
 * no imports, no browser. ⛔ THAT SPLIT IS NOT TIDINESS: Pipeline's laborclock
 * is an HTML app and cannot use a hook, but it prints to the same printer from
 * the same `dbo.PrintTemplate` rows and MUST agree on those three rules.
 */
/**
 * ⚠️ `getLocalDevices` HANDS BACK EITHER AN ARRAY OR AN OBJECT KEYED BY TYPE,
 * depending on whether a type filter was passed. Both shapes are handled rather
 * than assumed, because the one that is not handled fails as "no printers
 * found" — indistinguishable from an agent with nothing attached.
 */
function flattenDevices(result: ZebraDevice[] | Record<string, ZebraDevice[]>): ZebraDevice[] {
    if (Array.isArray(result)) return result;
    return Object.values(result ?? {}).flat().filter(Boolean);
}

export function useZebraPrint(opts: {
    getToken: () => Promise<string>;
    facilityCode: string;
}) {
    const { getToken, facilityCode } = opts;

    const [config, setConfig] = useState<PrintConfigDto | null>(null);
    const [agentReady, setAgentReady] = useState<boolean | null>(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    /* ⚠️ Devices are cached per mount, not per print. Enumerating on every job
       adds a round trip to the agent for a list that changes when somebody
       plugs in a printer, which is not mid-shift. */
    const devicesRef = useRef<ZebraDevice[] | null>(null);

    /* ---- the config, once ------------------------------------------------ */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const c = await getPrintConfig(await getToken(), facilityCode);
                if (!cancelled) setConfig(c);
            } catch {
                /* ⛔ NOT FATAL. The route already refuses to throw for a missing
                   table; this catch covers the network. Printing still works —
                   through the dialog — and `config === null` says we do not know
                   what was configured, which is different from "nothing was". */
                if (!cancelled) setConfig(null);
            }
        })();
        return () => { cancelled = true; };
    }, [getToken, facilityCode]);

    /* ---- is the agent there? --------------------------------------------- */
    const probeAgent = useCallback(async (): Promise<ZebraDevice[] | null> => {
        const sdk = window.BrowserPrint;
        /* ⛔ THE ONE FAILURE WE CAN NAME EXACTLY. No global means the two script
           tags did not load or the agent was never installed (§1AK.1 C1). */
        if (!sdk) { setAgentReady(false); return null; }

        if (devicesRef.current) return devicesRef.current;

        return new Promise<ZebraDevice[] | null>((resolve) => {
            /* ⚠️ THE SDK NEVER TIMES OUT ON ITS OWN. A machine where the
               certificate was not accepted (C2) or the origin is not whitelisted
               (C3) can leave the callback un-fired, and a print button that
               spins forever is worse than one that says it failed. */
            const timer = window.setTimeout(() => {
                setAgentReady(false);
                resolve(null);
            }, 5000);

            sdk.getLocalDevices(
                (result) => {
                    window.clearTimeout(timer);
                    const list = flattenDevices(result);
                    devicesRef.current = list;
                    setAgentReady(true);
                    resolve(list);
                },
                () => {
                    window.clearTimeout(timer);
                    setAgentReady(false);
                    resolve(null);
                },
                'printer',
            );
        });
    }, []);

    useEffect(() => { void probeAgent(); }, [probeAgent]);

    /**
     * ⭐⭐ WHICH DESTINATION FOR THIS DOCUMENT TYPE.
     *
     * ⛔ THIS MIRRORS `resolvePrintTarget` ON THE SERVER AND MUST NOT DRIFT.
     * ⚠️ The server owns the rule; this is the browser needing the same answer
     * before it has made a request. If they ever disagree, the server wins —
     * and the test pins both against the same table.
     */
    const destinationFor = useCallback((documentType: string): PrintDestinationDto | null => {
        if (!config) return null;
        const route = config.routes.find(
            r => r.documentType.toLowerCase() === documentType.trim().toLowerCase());
        if (!route) return null;
        return config.destinations.find(d => d.destinationCode === route.destinationCode) ?? null;
    }, [config]);

    /**
     * ---- ⭐ PRINT A LABEL --------------------------------------------------
     *
     * ⛔ RETURNS AN OUTCOME; IT DOES NOT THROW. Every caller is a button, and a
     * button needs a sentence, not a stack trace.
     */
    const printLabel = useCallback(async (args: {
        documentType: string;
        /** ⭐ Either a template code from `dbo.PrintTemplate`, or raw ZPL. */
        templateCode?: string;
        zpl?: string;
        values?: Record<string, unknown>;
    }): Promise<PrintOutcome> => {
        setBusy(true);
        setMessage(null);
        try {
            if (!config) {
                const m = 'Could not read the printer configuration, so this will use the printer dialog.';
                setMessage(m);
                return { ok: false, via: 'DIALOG', message: m };
            }
            if (!config.configRead) {
                const m = 'The printer configuration could not be read — migration 096 or its grant. '
                        + 'Use the printer dialog for now.';
                setMessage(m);
                return { ok: false, via: 'DIALOG', message: m };
            }

            const dest = destinationFor(args.documentType);
            if (!dest || dest.kind !== 'ZEBRA') {
                /* ⭐ NOT AN ERROR. No route, or a route to the dialog, is the
                   configured answer for this document type (§1AK.1 B3). */
                const m = `No label printer is configured for ${args.documentType} at this site, `
                        + 'so the printer dialog will open.';
                setMessage(m);
                return { ok: false, via: 'DIALOG', message: m };
            }

            const devices = await probeAgent();
            if (!devices) {
                const m = 'The print agent is not answering on this machine. Install Zebra Browser Print, '
                        + 'then accept the certificate at https://127.0.0.1:9101/ssl_support — see the '
                        + 'label printing runbook.';
                setMessage(m);
                return { ok: false, via: 'DIALOG', message: m };
            }

            /* ⚠️ Matched on the NAME the config holds. §1AK.1 B2 warns that this
               only works if every workstation calls the device the same thing —
               unverified, and this message is where that surfaces. */
            const wanted = (dest.printerName ?? '').trim().toLowerCase();
            const device = devices.find(d => (d.name ?? '').trim().toLowerCase() === wanted)
                ?? devices.find(d => (d.uid ?? '').trim().toLowerCase() === wanted);

            if (!device) {
                const names = devices.map(d => d.name ?? d.uid ?? '?').join(', ') || 'none';
                const m = `This machine has no printer called "${dest.printerName}". It reports: ${names}.`;
                setMessage(m);
                return { ok: false, via: 'DIALOG', message: m };
            }

            const template = args.templateCode
                ? config.templates.find(t => t.templateCode === args.templateCode)
                : undefined;
            const raw = args.zpl ?? template?.zpl;
            if (!raw) {
                const m = args.templateCode
                    ? `No label template called "${args.templateCode}" is configured at this site.`
                    : 'No label content was supplied.';
                setMessage(m);
                return { ok: false, via: 'DIALOG', message: m };
            }

            const body = renderZpl(raw, args.values ?? {});

            /* ⛔⛔ THE SAME CHECK THE DATABASE MAKES, REPEATED HERE ON PURPOSE.
               `CK_PrintTemplate_Zpl` guards the stored template; this guards the
               RENDERED result, which the database never sees. ⚠️ ZPL missing
               `^XZ` prints nothing and reports nothing — the printer waits for
               the rest of a command that never arrives, which is exactly what a
               dead printer looks like. */
            if (!isCompleteZpl(body)) {
                const m = 'The label content is not complete ZPL (it must contain ^XA and ^XZ), '
                        + 'so nothing was sent — an incomplete label prints silently as nothing.';
                setMessage(m);
                return { ok: false, via: 'DIALOG', message: m };
            }

            const sent = await new Promise<string | null>((resolve) => {
                const timer = window.setTimeout(() => resolve('The printer did not respond.'), 10000);
                device.send(
                    body,
                    () => { window.clearTimeout(timer); resolve(null); },
                    (err) => { window.clearTimeout(timer); resolve(err || 'The printer refused the label.'); },
                );
            });

            if (sent) {
                setMessage(sent);
                return { ok: false, via: 'ZEBRA', message: sent };
            }

            const m = `Sent to ${dest.displayName}.`;
            setMessage(m);
            return { ok: true, via: 'ZEBRA', message: m };
        } finally {
            setBusy(false);
        }
    }, [config, destinationFor, probeAgent]);

    return {
        /** ⚠️ `null` while probing — do not render a failure until it is `false`. */
        agentReady,
        config,
        busy,
        message,
        destinationFor,
        printLabel,
        /** ⭐ Exposed so a caller can say "no printer configured" before the click. */
        hasZebraFor: (documentType: string) => destinationFor(documentType)?.kind === 'ZEBRA',
    };
}
